import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'

/**
 * Initializes auth state listener and ensures user_settings row exists.
 * Call once at the app root.
 */
export function useAuth() {
  const { setSession, setAppReady, setAccessDenied, appReady } = useAuthStore()

  useEffect(() => {
    // GitHub OAuth Implicit flow: token は URL fragment (#access_token=...) で戻る。
    // supabase client は detectSessionInUrl=false なので手動で setSession を呼ぶ。
    // (Google Calendar OAuth は ?code= で別ページが処理するため衝突しない)
    const hash = window.location.hash
    if (hash && hash.includes('access_token=')) {
      const params = new URLSearchParams(hash.substring(1))
      const access_token = params.get('access_token')
      const refresh_token = params.get('refresh_token')
      if (access_token && refresh_token) {
        supabase.auth.setSession({ access_token, refresh_token }).then(({ data }) => {
          if (data.session) {
            setSession(data.session)
            ensureUserSettings(data.session.user.id, data.session.user.user_metadata)
          }
          window.history.replaceState({}, '', window.location.pathname + window.location.search)
        })
        return
      }
    }

    // Check existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) ensureUserSettings(session.user.id, session.user.user_metadata)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        if (session && !appReady) {
          ensureUserSettings(session.user.id, session.user.user_metadata)
        }
        if (!session) {
          setAppReady(false)
          setAccessDenied(false)
        }
      },
    )

    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function ensureUserSettings(userId: string, metadata: Record<string, unknown>) {
    const { data } = await supabase
      .from('user_settings')
      .select('id')
      .eq('user_id', userId)
      .single()

    if (!data) {
      const { error } = await supabase.from('user_settings').insert({
        user_id: userId,
        github_username: (metadata.user_name as string) || '',
      })
      if (error) {
        setAccessDenied(true)
        return
      }
    }

    setAppReady(true)
  }
}
